import { usePermissions } from '@/contexts/unified-permissions';
import { NotebookTabs, LibraryBig, GitBranch, BrickWall } from "lucide-react";
import { useSubscription } from "@/hooks/subscription";
import { useNavigate } from "react-router-dom";

const Features = () => {
  const {
    isAuthenticated
  } = usePermissions();
  const {
    isSubscribed,
    initiateSubscription
  } = useSubscription();
  const navigate = useNavigate();
  
  const handleFreeFeatureClick = () => {
    if (!isAuthenticated) {
      navigate('/auth/signup');
    } else {
      navigate('/journal');
    }
  };
  
  const handlePremiumFeatureClick = () => {
    if (!isAuthenticated) {
      navigate('/auth/signup');
    } else if (!isSubscribed) {
      initiateSubscription('monthly');
    } else {
      navigate('/game-planner');
    }
  };
  
  const features = [
    {
      title: "Training Journal",
      description: "Track your progress, log sessions, and reflect on your jiu-jitsu journey",
      icon: <NotebookTabs className="h-8 w-8 text-accent-purple" strokeWidth={1.5} />,
      premium: false,
      action: handleFreeFeatureClick,
      actionText: !isAuthenticated ? "Join Free" : "Go to Journal"
    }, 
    {
      title: "Technique Library",
      description: "Browse, study, and save techniques in an organized digital collection",
      icon: <LibraryBig className="h-8 w-8 text-secondary" strokeWidth={1.5} />,
      premium: false,
      action: handleFreeFeatureClick,
      actionText: !isAuthenticated ? "Join Free" : "Go to Journal"
    }, 
    {
      title: "Game Planner",
      description: "Visualize your strategy with interactive flowcharts and decision trees",
      icon: <GitBranch className="h-8 w-8 text-accent-purple" strokeWidth={1.5} />,
      premium: true,
      action: handlePremiumFeatureClick,
      actionText: !isAuthenticated ? "Sign Up Now" : !isSubscribed ? "Upgrade to Premium" : "Access Feature"
    }, 
    {
      title: "My Techniques",
      description: "Upload personal videos and create your own technique encyclopedia",
      icon: <BrickWall className="h-8 w-8 text-secondary" strokeWidth={1.5} />,
      premium: true,
      action: handlePremiumFeatureClick,
      actionText: !isAuthenticated ? "Sign Up Now" : !isSubscribed ? "Upgrade to Premium" : "Access Feature"
    }
  ];
  
  return (
    <section className="py-12 md:py-24 bg-background" id="features">
      <div className="container-padding">
        <div className="text-center mb-10 md:mb-16">
          <span className="bg-accent-purple/10 text-accent-purple px-4 py-1.5 rounded-full text-sm font-medium">
            FEATURE HIGHLIGHTS
          </span>
          <h2 className="heading-lg mt-4 md:mt-6">Supercharge Your Jiu Jitsu Journey</h2>
          <p className="text-neutral-600 mt-3 md:mt-4 max-w-2xl mx-auto text-sm md:text-base">
            Tools built by people who understand the journey — through plateaus, injuries, and breakthroughs.
          </p>
        </div>
        
        {/* Feature Tiles - Grid Layout */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {features.map((feature) => (
            <div 
              key={feature.title}
              className="bg-white rounded-xl border border-border p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow duration-300"
            >
              <div className="mb-4 bg-primary/5 p-4 rounded-full">
                {feature.icon}
              </div>
              
              <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
              
              {feature.premium && (
                <span className="bg-accent-purple/10 text-accent-purple px-3 py-1 rounded-full text-xs font-medium mb-3">
                  Premium
                </span>
              )}
              
              <p className="text-neutral-600 text-sm mb-6 flex-grow">{feature.description}</p>
              
              <button 
                onClick={feature.action}
                className="button-primary w-full text-sm py-2.5"
              >
                {feature.actionText}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
